import React from "react";

// Shared "Choose Ticket Option" block shown at the top of the Purchase/Reserve panels.
// Lets the buyer pick Single Day (the existing single-event flow) or a multi-event package.
// It only reports the selected key; each panel computes its own totals (people count differs).

const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  // Date-only model: events are stored at UTC midnight — format in UTC so the day doesn't shift.
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
};

const money = (n) => `$${Number(n || 0)}`;

// Price cell: Free when 0, else the offer price with the (higher) total struck through + saving.
function PriceCell({ free, offer, total, save }) {
  if (free) return <span className="fw-semibold text-success">Free</span>;
  const showStrike = Number(total) > Number(offer);
  return (
    <span className="d-inline-flex flex-column align-items-end">
      <span>
        {showStrike && (
          <span className="text-muted text-decoration-line-through me-1">{money(total)}</span>
        )}
        <strong>{money(offer)}</strong>
      </span>
      {Number(save) > 0 && <small className="text-success">Save {money(save)}</small>}
    </span>
  );
}

function ChooseTicketOption({ event, packages = [], isMember = false, selectedKey, onSelect, wrapperClassName = "px-4 pt-3" }) {
  if (!packages || packages.length === 0) return null; // No packages → keep the panel as-is.

  const eventDateLabel = event?.start_date ? `${fmtDate(event.start_date)} only · 1 ticket` : "1 ticket";
  const singleFree = isMember ? Number(event?.member_price || 0) === 0 : false;

  const Option = ({ value, title, subtitle, priceNode }) => {
    const active = selectedKey === value;
    return (
      <button
        type="button"
        onClick={() => onSelect(value)}
        className="w-100 text-start d-flex justify-content-between align-items-start"
        style={{
          border: `1px solid ${active ? "#F4C04A" : "var(--border-color)"}`,
          background: active ? "rgba(237,169,43,0.12)" : "#fff",
          borderRadius: 8,
          padding: "10px 12px",
          gap: 8,
          marginBottom: 8,
          cursor: "pointer",
        }}
      >
        <span className="d-flex align-items-start gap-2">
          <input type="radio" checked={active} readOnly style={{ marginTop: 4 }} />
          <span>
            <span className="d-block fw-semibold text-main">{title}</span>
            <small className="text-muted">{subtitle}</small>
          </span>
        </span>
        <span className="text-end">{priceNode}</span>
      </button>
    );
  };

  return (
    <div className={wrapperClassName}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h2 className="f-16 fw-semibold text-main mb-0">Choose Ticket Option</h2>
        <span className="badge bg-light text-muted">NEW</span>
      </div>

      <Option
        value="single"
        title="Single Day"
        subtitle={eventDateLabel}
        priceNode={
          <PriceCell free={singleFree} offer={isMember ? event?.member_price : event?.price} total={0} save={0} />
        }
      />

      {packages.map((pkg) => {
        const datesLabel = (pkg.included_events || []).map((e) => fmtDate(e.start_date)).join(", ");
        const offer = isMember ? pkg.offer_price_member : pkg.offer_price_nonmember;
        const total = isMember ? pkg.total_price_member : pkg.total_price_nonmember;
        const save = isMember ? pkg.save_member : pkg.save_nonmember;
        const free = isMember ? Number(pkg.offer_price_member || 0) === 0 : false;
        return (
          <Option
            key={pkg._id}
            value={pkg._id}
            title={pkg.name}
            subtitle={`${datesLabel} · ${pkg.event_count} tickets`}
            priceNode={<PriceCell free={free} offer={offer} total={total} save={save} />}
          />
        );
      })}
    </div>
  );
}

export default ChooseTicketOption;
